export interface UserRef {
  oid: string
  name?: string
  email?: string
}

/**
 * In-app authorization roles, held as grants on the user document. They
 * stack: PlatformAdmin implies StatusReportingAdmin, SurveyAdmin and
 * SurveyResults, and SurveyAdmin implies SurveyResults.
 * Everyone authenticated has `User`. Membership of the Entra admin group still
 * grants PlatformAdmin as break-glass, so a bad in-app assignment can never
 * lock everyone out.
 */
export type AppRole = 'PlatformAdmin' | 'StatusReportingAdmin' | 'SurveyAdmin' | 'SurveyResults' | 'User'

/** Most privileged first. */
export const APP_ROLES: AppRole[] = ['PlatformAdmin', 'StatusReportingAdmin', 'SurveyAdmin', 'SurveyResults', 'User']

export const ROLE_LABELS: Record<AppRole, string> = {
  PlatformAdmin: 'Platform admin',
  StatusReportingAdmin: 'Status reporting admin',
  // Sets up and sends surveys such as the leadership survey, and reads them.
  SurveyAdmin: 'Survey admin',
  // Reads the answers to those surveys.
  SurveyResults: 'Survey results',
  // Everyone signed in views and edits, so the default role is not a viewer.
  User: 'User',
}

/**
 * One role assignment. `scopes` is part of the stored shape, but no role in
 * this app is narrowed by it, so it is always empty.
 */
export interface RoleGrant {
  role: AppRole
  scopes: string[]
}

/**
 * A row in the self-seeded user directory (`GET /users`).
 * Keyed on Entra `oid` - the only stable identifier across renames.
 * `name`/`email` are display data, refreshed on every sign-in by the backend.
 */
export interface User {
  id: string  // equals oid
  oid: string
  name?: string
  email?: string
  firstSeenAt?: string
  lastSeenAt?: string
}

/** One role a person holds through one Entra group. */
export interface GroupGrant {
  role: AppRole
  scopes: string[]
  groupId: string
}

/** One Entra group standing in for a role (`GET /role-groups`, platform admin
 *  only). `groupId` is the object ID a sign-in token carries; `groupName` is
 *  display only. */
export interface RoleGroupMapping {
  groupId: string
  groupName: string
  role: AppRole
  scopes: string[]
}

export interface RoleGroupMap {
  id: string
  mappings: RoleGroupMapping[]
  /** The break-glass admin group, set in infrastructure rather than mapped
   *  here, so the page can name it instead of printing its object ID. */
  breakGlassGroupId?: string
  auditTrail: AuditEntry[]
}

/** A directory row with what its person's groups grant
 *  (`GET /users?includeRoles=true`, platform admin only). Someone in no mapped
 *  group holds nothing and is still a `User`. */
export interface DirectoryUser extends User {
  /** What their Entra group membership granted at their last sign-in, each
   *  naming the group it came from. Their whole access: nothing in the app
   *  assigns a role. */
  groupGrants?: GroupGrant[]
  /** When those were last read, which is that sign-in. */
  groupRolesAt?: string
  /** Append-only history of role changes. */
  auditTrail?: AuditEntry[]
}

/** `GET /me` - the caller's identity and server-resolved authorization. It
 *  comes from here, never from the ID token's `groups` claim. `roles` is the
 *  flattened list of the roles held by `grants`. */
export interface MeResponse extends UserRef {
  roles: AppRole[]
  grants: RoleGrant[]
}

/** One field change in a document's own embedded history, rendered on the
 *  product and project pages. The central log's row is `AuditLogEntry`. */
export interface AuditEntry {
  at: string
  actor: UserRef
  field: string
  before?: unknown
  after?: unknown
}

/** The entity types the central audit log covers. `drumbeat_template` only
 *  appears on rows written before the board date rules replaced it. */
export type AuditEntityType =
  | 'project'
  | 'product'
  | 'settings'
  | 'user'
  | 'reporting_window'
  | 'board_date_rules'
  | 'role_group_map'
  | 'drumbeat_template'

/** The entity types the audit page filters on. */
export const AUDIT_ENTITY_TYPES: AuditEntityType[] = [
  'project',
  'product',
  'settings',
  'user',
  'reporting_window',
  'board_date_rules',
  'role_group_map',
  'drumbeat_template',
]

/**
 * One row of the central audit log (`GET /audit`), platform admin only.
 * Written in addition to a document's own `auditTrail`, never instead of it.
 * `action` is 'create', 'update' or 'delete', 'roles_changed' for a grant
 * change, 'ddi_closed' / 'ddi_reopened' when a status reporting admin closes
 * or reopens a month's DDI texts, 'ddi_reset' when a platform admin resets
 * a month's DDI slide, and 'ddi_texts' / 'ddi_drafted' for the slide texts
 * a person wrote or the AI drafted. `field` is set when the action changed
 * one field and absent when it covers the whole document.
 */
export interface AuditLogEntry {
  id: string
  at: string
  actorOid?: string | null
  actorName?: string | null
  actorEmail?: string | null
  entityType: AuditEntityType
  entityId?: string | null
  entityName?: string | null
  action: string
  field?: string | null
  before?: unknown
  after?: unknown
}

/** Filters for `GET /audit`, all optional. `since` is an ISO date or
 *  timestamp. */
export interface AuditLogQuery {
  limit?: number
  offset?: number
  entityType?: AuditEntityType
  actorOid?: string
  since?: string
}

/** `total` counts every row matching the filters, not just this page. */
export interface AuditLogPage {
  entries: AuditLogEntry[]
  total: number
}

export type ProjectStatus = 'forecast' | 'complete' | 'cancelled'

// How adoption is measured for a project. See USER_GUIDE §6.5 for the why.
// - 'users'   - head-count based: expectedUsers + actualUsers, derived %
// - 'percent' - project lead has a % from a vendor dashboard; store it
//               directly in `adoptionRate`. `expectedUsers` is still needed
//               as the weight for the sum-then-divide roll-up.
// - 'na'      - no meaningful user base; project excluded from Adoption KPI.
export type AdoptionMode = 'users' | 'percent' | 'na'

// Legacy 3-level severity - superseded by the Danone impact x likelihood
// matrix. Only present on risks logged before the matrix existed.
export type RiskSeverity = 'low' | 'medium' | 'high'
// Danone risk assessment scale - shared by both axes, each scoring 1-4.
// Risk rating = impact score x likelihood score (see lib/risk.ts).
export type RiskLevel = 'very_low' | 'low' | 'high' | 'very_high'
// Category doubles as the forecast-KPI signal: an OPEN 'scope' risk projects
// an In Full miss, an OPEN 'cost' risk an At Cost miss (computeProjectedOTIFAC).
export type RiskCategory = 'delivery' | 'scope' | 'cost' | 'other'
export type RiskStatus = 'open' | 'mitigated' | 'closed'
/** RAG band a risk falls in, derived from impact x likelihood (lib/risk.ts).
 *  Declared here rather than imported so `types` stays free of dependencies on
 *  `lib`, which imports from it. */
export type RiskRagBand = 'green' | 'amber' | 'red'

export interface RiskEntry {
  id: string
  title: string
  severity?: RiskSeverity // legacy, pre-matrix entries only
  impact?: RiskLevel
  likelihood?: RiskLevel
  // Person accountable for the risk ("Owner" in the Danone register), picked
  // from the Entra user directory.
  owner?: UserRef
  // "Ability to handle the risk / Risk response" in the Danone register.
  // Mandatory in the UI for new/edited risks; optional here for legacy data.
  mitigation?: string
  // Mitigation deadline (ISO date) - "Deadline" in the Danone register.
  deadline?: string
  category: RiskCategory
  status: RiskStatus
  note?: string
  createdBy?: UserRef
  createdAt: string
  updatedAt?: string
}

// Danone 5-gate delivery lifecycle, shown as the ribbon across the top of the
// monthly status report. Deliberately ORTHOGONAL to `status`: `status` scopes
// the KPI maths, `stage` is the governance/communication signal. A project can
// be in 'scale' while still `forecast` - advanced, but not yet measured.
export type ProjectStage = 'discover' | 'design' | 'develop' | 'scale' | 'realise'

export const PROJECT_STAGES: { key: ProjectStage; label: string }[] = [
  { key: 'discover', label: 'Discover & Define' },
  { key: 'design', label: 'Design & Demonstrate' },
  { key: 'develop', label: 'Develop & Pilot' },
  { key: 'scale', label: 'Scale & Deploy' },
  { key: 'realise', label: 'Realise Value & Learn' },
]

export type MilestoneStatus = 'planned' | 'achieved' | 'at_risk' | 'missed'

/** One dated checkpoint on the delivery timeline. `date` is YYYY-MM-DD. */
export interface Milestone {
  id: string
  label: string
  date: string
  status: MilestoneStatus
}

/** A year's CAPEX/OPEX split. Per-year because status reports are always
 *  framed as "2026 CAPEX", and next year's ask is a separate conversation. */
export interface YearBudget {
  capex: number
  opex: number
  capexCommitted?: number
  opexCommitted?: number
}

export interface Project {
  id: string
  name: string
  description?: string
  asanaProjectId?: string
  subPortfolio: string
  tier: 'Black Line' | 'Winning' | 'Stretch' | 'License to Operate'
  status: ProjectStatus
  dueDate: string
  actualGoLive?: string
  deliveredInFull?: 'Y' | 'N'
  forecastCost: number
  actualCost?: number
  expectedUsers?: number
  actualUsers?: number
  adoptionMode?: AdoptionMode  // defaults to 'users' when absent
  adoptionRate?: number        // 0-100, only set when adoptionMode === 'percent'
  expectedValue: number
  tradeSpendReduction?: number  // RGM only
  growth?: number               // RGM only
  valueRealised?: number
  stakeholders: string[]
  // Opt-out flag for back-loaded / legacy projects. True = no survey flow,
  // no "needs configuration" nag, no Responses step in the pipeline.
  skipStakeholders?: boolean
  surveyToken: string
  stakeholderTokens?: Record<string, string>
  surveySentAt?: string
  surveyEmailsSent?: string[]
  lastSyncedAt?: string
  templateMatch?: boolean
  templateCheckedAt?: string
  productId?: string
  team?: string
  createdBy?: UserRef
  auditTrail?: AuditEntry[]
  lastMeaningfulUpdate?: string
  /**
   * Ledger keyed by "YYYY-MM". Missing months are treated as zero-base,
   * zero-adjustment. See `MonthlyLedgerEntry` for the field semantics.
   */
  monthlyLedger?: Record<string, MonthlyLedgerEntry>
  /**
   * Optional split of hours-saved value across recipient sub-portfolios.
   * Keys are sub-portfolio names; values are percentages that must sum to 100.
   * When absent, 100% of hours is attributed to `subPortfolio` (the project
   * owner) - identical to existing behaviour. Only affects the "Received by"
   * reporting mode; OTIF / At Cost / Satisfaction always route by `subPortfolio`.
   */
  valueSubPortfolios?: Record<string, number>
  risks?: RiskEntry[]
  /**
   * Monthly status reporting opt-in. Only the "big" projects file a report -
   * a plain flag rather than deriving from tier, so re-tiering a project never
   * silently changes who owes one.
   */
  reportable?: boolean
  /** Included in the monthly DDI portfolio slide. Opt-in like `reportable`. */
  ddi?: boolean
  stage?: ProjectStage
  milestones?: Milestone[]
  /** Programme window, shown as "Start → End" on the status slide. Both are
   *  distinct from `dueDate`, the go-live commitment: a multi-year programme
   *  runs on well past the date its product goes live. */
  startDate?: string
  endDate?: string
  /** Keyed by 4-digit year: { "2026": { capex: 700000, opex: 0 } } */
  budgetByYear?: Record<string, YearBudget>
  functionalVP?: UserRef
  /** Business-side lead. Distinct from the sponsoring VP and the delivery
   *  PM; all three are named on the status slide. */
  functionalLead?: UserRef
  projectManager?: UserRef
}

// ── Monthly status report ────────────────────────────────────────────────────

/** Reported RAG. Distinct from the risk register's RiskRag (which is derived
 *  from impact x likelihood) - this is a position, computed or asserted. */
export type RagStatus = 'green' | 'amber' | 'red'

/** Report lifecycle - the update → review chain that currently lives as a
 *  yellow annotation on the PowerPoint slide. */
export type ReportState = 'draft' | 'submitted' | 'reviewed'

/** Granularity of the dates shown on the slide. */
/** How exactly the slide states the dates. `quarter` for work only
 *  committed to a quarter; `tbc` when the PM does not want a date published
 *  yet, whatever the record holds. */
export type DatePrecision = 'day' | 'month' | 'quarter' | 'tbc'

/**
 * One dimension's RAG, keeping the derived value and any human override side
 * by side. Never collapse these: when a PM marks Cost green while the risk log
 * says red, that disagreement is the most interesting signal in the report -
 * either the risk log is stale or the PM is carrying something unwritten.
 */
export interface RagAssessment {
  derived: RagStatus
  derivedReason: string
  override?: RagStatus
  overrideReason?: string
}

/** A highlight / lowlight / support-needed line. `riskId` is set when the
 *  bullet came from the risk log, so the slide can link back to the source. */
export interface ReportBullet {
  id: string
  text: string
  riskId?: string
}

/** Project facts frozen at save time so re-opening June's report in September
 *  renders June's numbers. Anything the slide shows that could drift lives here. */
/** An open risk as it stood when the report was published. Frozen like the
 *  rest of the snapshot: risks get closed and re-rated constantly, and June's
 *  slide must keep showing June's risk picture. */
export interface ReportRisk {
  id: string
  /** Wording shown on the slide - the register's title unless reworded. */
  title: string
  /** The register's own wording, kept whenever `title` overrides it, so the
   *  provenance of a reworded risk is never lost. */
  sourceTitle?: string
  /** Danone RAG band, derived from impact x likelihood at publish time. */
  rag: RiskRagBand
  /** Effective 1-16 rating, so the slide can sort and show the number. */
  score: number
  owner?: string
  category: RiskCategory
}

export interface ReportSnapshot {
  projectName: string
  subPortfolio?: string
  tier?: string
  stage?: ProjectStage
  functionalVP?: string
  functionalLead?: string
  projectManager?: string
  startDate?: string
  endDate?: string
  goLive?: string
  budgetYear?: string
  capex: number
  opex: number
  capexCommitted?: number
  opexCommitted?: number
  milestones: Milestone[]
  /** Top open risks by rating. Absent on reports filed before risks were
   *  shown on the slide. */
  risks?: ReportRisk[]
}

export interface ProjectReport {
  id: string          // "{projectId}_{YYYY-MM}"
  projectId: string
  month: string       // YYYY-MM - the reporting period
  /** The date the report was written - NOT the period. Governance decks are
   *  dated to the meeting ("Report Date: 19th June"). */
  reportDate?: string
  /** How precisely dates read on the slide. Multi-year programmes are usually
   *  committed to a month, and "Sep 2025 → Dec 2027" promises less than
   *  "2 Sep 2025 → 22 Dec 2027". */
  datePrecision?: DatePrecision
  /** Leave the Open Risks block off the slide entirely. */
  hideRisks?: boolean
  /** Per-risk replacement wording for the slide, keyed on risk id. Register
   *  titles are written for tracking; a board slide often needs different
   *  words for the same risk, and editing the register to suit one audience
   *  would be worse. */
  riskLabels?: Record<string, string>
  /** DDI portfolio texts. Edited centrally on the DDI page via PATCH .../ddi;
   *  the report editor's PUT preserves them server-side. */
  ddiSummary?: string
  ddiNextSteps?: string
  /** What the AI draft's check asked a person to look at closely. Cleared by
   *  the next save from the DDI page. */
  ddiAiIssues?: string[]
  /** Who last wrote the DDI slide texts, separately from the report itself. */
  ddiUpdatedBy?: UserRef
  ddiUpdatedAt?: string
  overall: RagStatus
  /** Carried forward from last month at prefill so the slide can say
   *  "unchanged since May" without a second lookup at render time. */
  overallPrevious?: RagStatus
  /** The one-line "why", pulled out above the narrative. */
  whyLine: string
  narrative: string
  scope: RagAssessment
  schedule: RagAssessment
  cost: RagAssessment
  benefit: RagAssessment
  highlights: ReportBullet[]
  lowlights: ReportBullet[]
  supportNeeded: ReportBullet[]
  snapshot: ReportSnapshot
  state: ReportState
  submittedBy?: UserRef
  submittedAt?: string
  /** Submitted after the month's due time but before it locked. Accepted,
   *  and flagged "after cut-off" on the month-end review. */
  submittedAfterDue?: boolean
  reviewedBy?: UserRef
  reviewedAt?: string
  updatedBy?: UserRef
  updatedAt?: string
}

/**
 * One month's slice of a project's hours-saved ledger.
 * - `base` is auto-seeded when the project completes: `expectedValue / 12`
 *   spread from the go-live month through December, plus full forecast years.
 * - `adjustments` is the manual correction layered on top (±).
 *   Month total = base + adjustments.
 * - `adjustmentNotes` captures why, one entry per batch of edits that
 *   touches this month. Never an empty string.
 */
export interface MonthlyLedgerEntry {
  base: number
  adjustments: number
  adjustmentNotes: string[]
}

export interface Product {
  id: string
  name: string
  vendor?: string
  description?: string
  ownerRef: UserRef
  contributors: UserRef[]
  createdBy: UserRef
  createdAt?: string
  /**
   * Append-only history of field changes: name, vendor, description,
   * owner, contributors.
   */
  auditTrail?: AuditEntry[]
}

export interface FieldMapping {
  id: string
  projectId: string
  asanaProjectId: string
  mappings: {
    dueDate: string | null
    actualGoLive: string | null
    forecastCost: string | null
    actualCost: string | null
    subPortfolio: string | null
    tier: string | null
    deliveredInFull: string | null
    expectedValue: string | null
    adoptionRate: string | null
  }
}

export interface SurveyResponse {
  id: string
  projectId: string
  respondentEmail: string
  techSatisfaction: number    // 1-5: how well solution meets business needs
  peopleSatisfaction: number  // 1-5: project team professionalism & communication
  adoption?: number           // number of active users (not a percentage)
  valueRealised?: number
  additionalComments?: string // optional free-text feedback from respondent
  submittedAt: string
  submissionHash?: string     // SHA-256 of recipient token - for deduplication only, token is not recoverable
}

export interface Portfolio {
  name: string
  subPortfolios: string[]
}

export interface Settings {
  subPortfolios: string[]
  portfolios: Portfolio[]
  onTimeDays: number
  atCostPct: number
  // Minimum risk rating (impact x likelihood, 1-16) an open risk must reach to
  // project a Forecast OTIFAC miss. 4 = Amber+ (default), 9 = Red only, 1 = any.
  riskForecastMinScore?: number
  powerAutomateUrl?: string
  /** The form the sidebar's "Request access" link opens. Hidden while unset. */
  requestAccessUrl?: string
  teams: string[]
  valueRealisedTargetHrs: number
  valueRealisedTargetStartYear?: number
  valueRealisedTargetEndYear?: number
  // Monthly update-reminder email (last working day, via the survey webhook)
  reminderEnabled?: boolean
  reminderGroupEmail?: string
  // Legacy list of who could set reporting windows before that became the
  // StatusReportingAdmin role. Carried across to grants on startup and then
  // emptied, so it is only ever read.
}

export interface AsanaProject {
  gid: string
  name: string
  due_on?: string
  custom_fields?: AsanaCustomField[]
}

export interface AsanaCustomField {
  gid: string
  name: string
  type: string
  format?: string
  display_value?: string
  number_value?: number
  currency_code?: string
  precision?: number
  enum_value?: { name: string }
  date_value?: { date: string }
}

export interface SavedViewFilters {
  search: string
  portfolio: string[]
  tier: string[]
  status: string[]
  source: string[]
  team: string[]
  owner?: string[]
}

export interface SavedView {
  id: string
  userId: string
  name: string
  page: string
  filters: SavedViewFilters
  createdAt: string
  updatedAt: string
}

/** One project's month-end sign-off: who approved it and when. */
export interface ReviewMark {
  by: UserRef
  at: string
}

/** How many working days before a month's DDI Board its reports are due and
 *  lock, its own document in the settings container (id `board-date-rules`)
 *  so status reporting admins can edit it without the admin-only settings
 *  write. Working days skip weekends and England and Wales bank holidays. The
 *  rules only suggest dates: months already saved keep theirs. */
export interface BoardDateRules {
  id: string
  dueWorkingDaysBefore: number
  lockWorkingDaysBefore: number
  auditTrail: AuditEntry[]
}

/** Body of `PUT /board-date-rules`. Whole numbers from 0 to 30, lock no more
 *  than due. */
export type BoardDateRulesUpdate = Pick<BoardDateRules, 'dueWorkingDaysBefore' | 'lockWorkingDaysBefore'>

/** When a month's status reports may be submitted and validated. Every
 *  instant is Europe/London wall-clock time: `opensAt` is 00:00 on the 1st of
 *  the month, `closesAt` 17:00 on the lock date. */
export interface ReportingWindow {
  opensAt: string
  closesAt: string
  /** Soft deadline, 17:00 on the due date: shown, and a submission after it
   *  is accepted but flagged. `closesAt` stays the enforced one. */
  dueAt?: string | null
  /** YYYY-MM-DD. The rules count back from it to suggest the due and lock
   *  dates. */
  ddiBoardDate?: string | null
  /** The due or lock date was typed by hand, so a new board date keeps it.
   *  Absent on windows saved before the marks existed. */
  dueMoved?: boolean | null
  lockMoved?: boolean | null
  /** When a status reporting admin closed the month's DDI texts, and who.
   *  Both absent while the texts are open. */
  ddiClosedAt?: string | null
  ddiClosedBy?: UserRef | null
  note?: string | null
  setBy: UserRef
  setAt: string
  announcedAt?: string | null
}

/** Body of `PUT /month-reviews/{month}/window`, dates as YYYY-MM-DD. The
 *  server derives the instants; a save never reopens DDI texts closed by
 *  hand. */
export interface ReportingWindowUpdate {
  ddiBoardDate: string | null
  dueDate: string | null
  lockDate: string
  dueMoved: boolean
  lockMoved: boolean
  note: string | null
}

/** A per-project later close for the month, granted by a status reporting
 *  admin. */
export interface ReportingExtension {
  closesAt: string
  reason: string
  grantedBy: UserRef
  grantedAt: string
  notifiedAt?: string | null
}

/**
 * The month-end review doc - one per month, id = "YYYY-MM".
 * `reviews` maps projectId → ReviewMark. Stored in its own container so
 * ticking "reviewed" never bumps a project's lastMeaningfulUpdate.
 */
export interface MonthReview {
  id: string
  reviews: Record<string, ReviewMark>
  reportingWindow?: ReportingWindow | null
  extensions?: Record<string, ReportingExtension>
  /** When, and by whom, the DDI slide was drafted with AI. Offered once a month. */
  ddiDraftedAt?: string | null
  ddiDraftedBy?: UserRef | null
}

/** Outcome of drafting the DDI slide with AI. `failed` names the rows to write
 *  by hand; `flagged` the rows whose check found something to look closely at. */
export interface DdiDraftResult {
  review: MonthReview
  drafted: number
  failed: string[]
  flagged: { row: string; issues: string[] }[]
}

export type DdiDraftStage = 'gather' | 'draft' | 'check' | 'save'

/** One progress line streamed while the slide is drafted. */
export interface DdiDraftEvent {
  stage: DdiDraftStage
  status: 'running' | 'done'
  detail?: string
  done?: number
  total?: number
  row?: string
}

/** Response of the window and extension writes: the doc plus whether the
 *  email actually went out, so a skipped send is never mistaken for one. */
export interface WindowSaveResult {
  review: MonthReview
  emailSent: boolean
  emailDetail?: string | null
}

export interface BauSnapshot {
  id: string
  syncedAt: string
  openByPriority: Record<string, number>
  currentMonthTotal: number
  lastMonthTotal: number
  currentSlaBreaches: number
  lastMonthSlaBreaches: number
  byType: { type: string; count: number }[]
  byService: { service: string; count: number }[]
  byAssignmentGroup: { group: string; count: number }[]
  monthlyTrend: { month: string; total: number; breached: number }[]
  incidentByMonthPriority: { month: string; priority: string; count: number }[]
  incidentSlaByMonth: { month: string; total: number; breached: number }[]
  groupMonthlyTrend: { group: string; month: string; total: number; breached: number }[]
  groupIncidentPriority?: { group: string; month: string; priority: string; count: number }[]
  groupIncidentSla?: { group: string; month: string; total: number; breached: number }[]
  groupByService?: { group: string; month: string; service: string; count: number }[]
  groupByType?: { group: string; month: string; type: string; count: number }[]
}

// One Jira issue, trimmed to what the Operational page renders. Dates are
// YYYY-MM-DD (or null). `statusCategory` is Jira's coarse bucket
// (new | indeterminate | done). `priority` is the API value, not the board
// grouping - a board "Minor" ticket can carry a real P2 here.
export interface JiraIssue {
  key: string
  created: string | null
  updated: string | null
  resolved: string | null
  type: string
  status: string
  statusCategory: string
  priority: string | null
  summary: string
  assignee: string | null
  reporter: string | null
  labels: string[]
}

// Jira (Commercial AEM BAU) snapshot - one Cosmos doc overwritten daily by the
// sync job. `issues` is newest-first and may be trimmed to fit Cosmos' 2 MB
// doc limit (`droppedForSize` counts any oldest issues dropped). The distinct
// value arrays feed the filter dropdowns.
export interface JiraSnapshot {
  id: string
  syncedAt: string
  browseBaseUrl: string
  projectKey: string
  issues: JiraIssue[]
  storedCount: number
  droppedForSize?: number
  labels: string[]
  types: string[]
  statuses: string[]
  priorities: string[]
}

// ── Surveys sent by hand (the leadership survey and others like it) ─────────
// A survey type holds the design; each round is one sending of it with its own
// copy of that design. Answers are anonymous: a submission carries only the
// department it came from, its partner team, its answers and the day.

export type SurveyQuestionKind = 'rating' | 'scale' | 'text' | 'choice'
export type SurveyRoundStatus = 'draft' | 'open' | 'closed'
/** A star count or 0-10 score, a chosen option or free text, "na", or null when skipped. */
export type SurveyAnswer = number | string | null

export interface SurveyQuestion {
  id: string
  kind: SurveyQuestionKind
  /** A short name, such as "Overall experience", for report columns. */
  title: string
  /** May say {partner}: the answering person's partner team. */
  text: string
  required: boolean
  /** rating: one label per star, lowest first. */
  labels: string[]
  /** scale: always 0-10; these name the two ends. */
  lowLabel: string
  highLabel: string
  /** choice: the options. */
  options: string[]
  /** When set, a not-applicable answer is offered under this label. */
  naLabel: string
}

export interface SurveyPartner {
  id: string
  name: string
  /** For reference in setup only. Feedback is on teams, so neither the
   *  survey nor the results show it. */
  lead: string
  /** The Settings team this partner is, if any. */
  team: string
}

/** Part of a department with its own partner team, such as CCD's Back
 *  Office. People in a split department choose one area to answer for. */
export interface SurveyArea {
  id: string
  name: string
  /** What the area covers, so people choose the right one. */
  definition: string
  partnerId: string
}

export interface SurveyDepartment {
  id: string
  name: string
  /** Blank = no partner team; questions name the fallback ("IT"). */
  partnerId: string
  /** The Settings portfolio this department is, if any. */
  portfolio: string
  /** Split into areas (two or more), each with its own partner team. */
  areas?: SurveyArea[]
}

export interface SurveyDesign {
  partners: SurveyPartner[]
  departments: SurveyDepartment[]
  questions: SurveyQuestion[]
  emailSubject: string
  emailBody: string
  partnerFallback: string
}

export interface SurveyType extends SurveyDesign {
  id: string
  name: string
  description: string
  createdAt?: string
  createdBy?: UserRef
  updatedAt?: string
  updatedBy?: UserRef
}

export interface SurveyRecipient {
  name: string
  email: string
  departmentId: string
  /** On the Board, answering from their own department as well. */
  board?: boolean
  invitedAt?: string | null
}

export interface SurveyRound extends SurveyDesign {
  id: string
  typeId: string
  typeName: string
  name: string
  status: SurveyRoundStatus
  recipients: SurveyRecipient[]
  /** Department id -> people sent their link / answers received, then the
   *  Board members among them. */
  invited: Record<string, number>
  responded: Record<string, number>
  invitedBoard?: Record<string, number>
  respondedBoard?: Record<string, number>
  sentAt?: string | null
  sentBy?: UserRef | null
  reminders: { at: string; by?: UserRef | null; sent: number }[]
  closedAt?: string | null
  closedBy?: UserRef | null
  createdAt?: string
  updatedAt?: string
}

export interface SurveySendResult {
  sent: string[]
  failed: string[]
  round: SurveyRound
}

export interface SurveyFormArea {
  id: string
  /** "CCD Back Office" */
  name: string
  definition: string
  /** The partner team's name, what it supports, and the words {partner}
   *  stands for once chosen. */
  team: string
  supports?: string[]
  partner: string
}

/** What a survey link opens. The questions keep {partner}: the page shows
 *  `team` as a tag there, or the words `partner` when there is no team. In a
 *  split department one of `areas` is chosen first. */
export interface SurveyForm {
  roundName: string
  typeName: string
  status: SurveyRoundStatus
  submitted: boolean
  department?: string
  partner: string
  team?: string
  /** The departments (and areas) the team supports. */
  supports?: string[]
  areas?: SurveyFormArea[]
  questions: SurveyQuestion[]
}

export interface SurveySubmission {
  id: string
  departmentId: string
  partnerId: string
  /** The area it was given for, in a split department. */
  area?: string
  /** Given by a Board member. */
  board?: boolean
  answers: Record<string, SurveyAnswer>
  /** The day only. */
  submittedOn: string
}

export interface SurveyRoundResults extends SurveyDesign {
  id: string
  name: string
  status: SurveyRoundStatus
  sentAt?: string | null
  closedAt?: string | null
  invited: Record<string, number>
  responded: Record<string, number>
  invitedBoard?: Record<string, number>
  respondedBoard?: Record<string, number>
  submissions: SurveySubmission[]
}

export interface SurveyTypeResults {
  id: string
  name: string
  description: string
  /** Sent rounds, oldest first. */
  rounds: SurveyRoundResults[]
}

export interface SurveyTypeSummary {
  id: string
  name: string
  description: string
  rounds: number
}
